import type { IconSvgElement } from '@/components/ui/icon';
import {
  Award01IconSolid,
  ChartRoseIconSolid,
  Home01Icon,
  MoneyBag01IconSolid,
  Pulse02Icon,
  Rocket01IconSolid,
  StarAward01IconSolid,
} from '@/components/ui/icon/glyphs';
import { stripDemoModePath } from '@/lib/network';

type NavTranslationKey =
  | 'home'
  | 'live'
  | 'markets'
  | 'launchpad'
  | 'winners'
  | 'social'
  | 'portfolio'
  | 'how_it_works';

interface NavItem {
  key: `nav.${NavTranslationKey}`;
  label: NavTranslationKey;
  href: string;
  icon: IconSvgElement;
  mobileBottomBar: boolean;
  desktopHeader: boolean;
  liveEventsOnly?: boolean;
  hiddenWhenLiveEvents?: boolean;
}

/** Category chips apply only on the markets grid (homepage + `/markets`), not on
 *  `/markets/[pubkey]` or unrelated routes. Matches logo → `/` and main-nav Markets scope
 *  for listing surfaces; detail URLs stay out so "All" is not highlighted there. */
export function isMarketsCategoryListingSurface(pathname: string): boolean {
  const bare = stripDemoModePath(pathname);
  return bare === '/' || bare === '' || bare === '/markets';
}

/** The four listing surfaces whose feed the global category nav filters in
 *  place via `?category=`. */
export type CategoryFilterSurface = '/live' | '/markets' | '/winners' | '/proposals';

/** Map a network-aware pathname (no locale, no query) to the listing surface
 *  whose feed the category nav should filter in-place — or `null` for detail
 *  pages and every unrelated route, where the nav falls back to navigating to
 *  `/markets`. The winners/proposals matches are EXACT (not `startsWith`), so
 *  `/winners/[mint]` and `/proposals/[proposalId]` fall through to `null`. */
export function categoryFilterSurface(pathname: string): CategoryFilterSurface | null {
  if (isMarketsCategoryListingSurface(pathname)) return '/markets';
  const bare = stripDemoModePath(pathname);
  if (bare === '/live') return '/live';
  if (bare === '/winners') return '/winners';
  if (bare === '/proposals') return '/proposals';
  return null;
}

/** True iff the category nav filters the current surface's feed in place
 *  (i.e. not a detail/unrelated route where it acts as a pure navigator). */
export function isCategoryFilterListingSurface(pathname: string): boolean {
  return categoryFilterSurface(pathname) !== null;
}

/** The "core" surfaces where the mobile bottom nav is shown: the markets feed
 *  (incl. the `/` home, which renders the same markets list), the proposals
 *  feed, the winners feed, and the viewer's OWN portfolio. Everywhere else
 *  (detail pages, other users' portfolios, settings, social, …) the bar is
 *  hidden and users navigate via back/forward. `ownUserId` is the authenticated
 *  user's id (null when logged out → portfolio is then never a match). Own
 *  portfolio is `/portfolio` (the `OwnPortfolio` route) or `/portfolio/<ownId>`;
 *  `/portfolio/<otherId>` is somebody else's and stays hidden. */
export function isCoreMobileNavSurface(pathname: string, ownUserId: string | null): boolean {
  if (categoryFilterSurface(pathname) !== null) return true;
  const bare = stripDemoModePath(pathname);
  if (bare === '/portfolio') return true;
  return ownUserId !== null && bare === `/portfolio/${ownUserId}`;
}

/** The listing surface a category click should TARGET — broader than
 *  {@link categoryFilterSurface}: a DETAIL page resolves to its parent listing
 *  (so `/winners/<mint>` → `/winners`, `/proposals/<id>` → `/proposals`,
 *  `/markets/<pubkey>` → `/markets`). Everything unrelated (social, portfolio,
 *  …) defaults to `/markets`. Always returns a concrete base (never null) since
 *  the nav must always have somewhere to send the click. Distinct from
 *  `categoryFilterSurface` (exact-match), which drives in-place highlighting and
 *  stays `null` on detail pages — a detail page is a launcher, not a filtered
 *  list, so no chip is highlighted there. */
export function categoryNavTargetBase(pathname: string): CategoryFilterSurface {
  const bare = stripDemoModePath(pathname);
  if (bare === '/winners' || bare.startsWith('/winners/')) return '/winners';
  if (bare === '/proposals' || bare.startsWith('/proposals/')) return '/proposals';
  if (bare === '/live' || bare.startsWith('/live/')) return '/live';
  return '/markets';
}

/**
 * Returns whether a category pill on this page switches the category in place
 * instead of navigating.
 *
 * @remarks
 * Only on the feed a pill targets does a click change nothing but `?category=`,
 * which the feeds read from the URL. The demo home targets `/markets`, so its
 * pills keep navigating; `/live` seeds its category from the server once and
 * ignores later URL changes, so it keeps navigating too.
 *
 * @param pathname - The current path, with or without the `/demo-mode` prefix.
 *
 * @example
 * ```ts
 * categorySwitchesInPlace('/demo-mode/winners'); // true
 * categorySwitchesInPlace('/markets/abc'); // false: a detail page opens /markets
 * ```
 */
export function categorySwitchesInPlace(pathname: string): boolean {
  const target = categoryNavTargetBase(pathname);
  return target !== '/live' && stripDemoModePath(pathname) === target;
}

/** The bare nav hrefs of the four filterable listing surfaces. */
const CATEGORY_FILTERABLE_HREFS: ReadonlySet<string> = new Set([
  '/markets',
  '/live',
  '/winners',
  '/proposals',
]);

/** Carry the active `?category=` onto a main-nav link so the global category
 *  filter PERSISTS when moving between the filterable listing surfaces — e.g.
 *  from `/winners?category=Economics` clicking "Proposals" lands on
 *  `/proposals?category=Economics`. Non-filterable targets (social, portfolio,
 *  detail pages, …) get the bare href; navigating there clears the filter. The
 *  raw value is carried verbatim (CSV multi-select preserved); the destination
 *  validates + drops unknown categories. */
export function navHrefWithCategory(href: string, category: string | null): string {
  if (category === null || category.length === 0) return href;
  if (!CATEGORY_FILTERABLE_HREFS.has(href)) return href;
  return `${href}?category=${encodeURIComponent(category)}`;
}

/** Pathname from `@/i18n/navigation` usePathname (no locale prefix, no query).
 *  Strips the `/demo-mode/` prefix before matching so demo URLs highlight
 *  the same nav item as their bare equivalents (e.g. `/demo-mode/proposals`
 *  highlights "Proposals" the same as `/proposals`). */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const bare = stripDemoModePath(pathname);
  if (item.key === 'nav.markets') {
    return isMarketsCategoryListingSurface(pathname) || bare.startsWith('/markets/');
  }
  if (item.href === '/') {
    return bare === '/' || bare === '';
  }
  return bare === item.href || bare.startsWith(`${item.href}/`);
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'nav.home',
    label: 'home',
    href: '/',
    icon: Home01Icon,
    mobileBottomBar: false,
    desktopHeader: false,
  },
  {
    key: 'nav.live',
    label: 'live',
    href: '/live',
    icon: Pulse02Icon,
    mobileBottomBar: true,
    desktopHeader: true,
    liveEventsOnly: true,
  },
  {
    key: 'nav.markets',
    label: 'markets',
    href: '/markets',
    icon: ChartRoseIconSolid,
    mobileBottomBar: true,
    desktopHeader: true,
  },
  {
    key: 'nav.launchpad',
    label: 'launchpad',
    href: '/proposals',
    icon: Rocket01IconSolid,
    mobileBottomBar: true,
    desktopHeader: true,
  },
  {
    key: 'nav.winners',
    label: 'winners',
    href: '/winners',
    icon: Award01IconSolid,
    mobileBottomBar: true,
    desktopHeader: true,
  },
  {
    key: 'nav.social',
    label: 'social',
    href: '/social',
    icon: StarAward01IconSolid,
    // Winners takes Social's slot in the mobile bottom bar. Social remains in
    // the desktop header and is intentionally absent from the launch sidebar.
    mobileBottomBar: false,
    desktopHeader: true,
  },
  {
    key: 'nav.portfolio',
    label: 'portfolio',
    href: '/portfolio',
    icon: MoneyBag01IconSolid,
    mobileBottomBar: true,
    desktopHeader: false,
    hiddenWhenLiveEvents: true,
  },
] as const;

export function desktopNavigationItems(liveEventsEnabled: boolean): readonly NavItem[] {
  return NAV_ITEMS.filter(
    (item) => item.desktopHeader && (!item.liveEventsOnly || liveEventsEnabled),
  );
}

export function mobileNavigationItems(
  liveEventsEnabled: boolean,
  isAuthenticated: boolean,
): readonly NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      item.mobileBottomBar &&
      (!item.liveEventsOnly || liveEventsEnabled) &&
      (!item.hiddenWhenLiveEvents || !liveEventsEnabled) &&
      (isAuthenticated || item.key !== 'nav.portfolio'),
  );
}

export type { NavItem };
